import React, { useState, useEffect } from "react";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle2, XCircle, ChevronDown, ChevronUp } from "lucide-react";
import {
  computeEliminationAnswer,
  evaluateTrigger,
} from "@/lib/pharmacyFirst/engine/eliminationRouting";
import type {
  DrugEliminationNode as DrugEliminationNodeType,
  PatientInput,
} from "@/lib/pharmacyFirst/types";
import type { EliminationLogEntry } from "@/lib/pharmacyFirst/types/audit.types";
import { getSelectedAnswerClasses } from "./answerTone";

export { evaluateTrigger };

export interface DrugEliminationAnswer {
  answer: boolean;
  eliminatedDrugIds: string[];
  logEntries: {
    ruleId: string;
    drugIds: string[];
    reason: string;
    answer: boolean;
  }[];
  managementPlan: {
    planText: string;
    patientAcknowledged: boolean;
  } | null;
}

interface DrugEliminationNodeProps {
  node: DrugEliminationNodeType;
  eligibleDrugIds: string[];
  drugLabels: Record<string, string>;
  isDarkMode?: boolean;
  stepNumber?: number;
  /** Previously given answer, re-seeded when the pharmacist steps back. */
  value?: DrugEliminationAnswer | null;
  onAnswer: (answer: DrugEliminationAnswer) => void;
  onPreviewChange?: (preview: {
    eligibleDrugIds: string[];
    eliminatedDrugIds: string[];
    logEntries: EliminationLogEntry[];
  } | null) => void;
  evaluationPatient?: PatientInput;
  evaluationContext?: Record<string, unknown>;
}

function EligibilityPanel({
  eligibleBefore,
  eliminatedDrugIds,
  logEntries,
  drugLabels,
  isDarkMode,
}: {
  eligibleBefore: string[];
  eliminatedDrugIds: string[];
  logEntries: DrugEliminationAnswer["logEntries"];
  drugLabels: Record<string, string>;
  isDarkMode: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const remainingIds = eligibleBefore.filter(
    (id) => !eliminatedDrugIds.includes(id)
  );
  const hasEliminations = eliminatedDrugIds.length > 0;

  return (
    <div
      className={`mt-4 border rounded-lg overflow-hidden ${
        isDarkMode ? "border-gray-700" : "border-gray-200"
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`w-full flex items-center justify-between px-3 py-2 text-xs font-medium ${
          isDarkMode
            ? "bg-gray-750 text-gray-300 hover:bg-gray-700"
            : "bg-gray-50 text-gray-600 hover:bg-gray-100"
        }`}
      >
        <span>
          {remainingIds.length} drug{remainingIds.length !== 1 ? "s" : ""} still
          eligible
          {hasEliminations && ` · ${eliminatedDrugIds.length} removed`}
        </span>
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
      </button>

      {expanded && (
        <div
          className={`px-3 py-2 space-y-1.5 ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          {remainingIds.map((id) => (
            <div key={id} className="flex items-center gap-2">
              <CheckCircle2
                className={`h-3.5 w-3.5 flex-shrink-0 ${
                  isDarkMode ? "text-emerald-400" : "text-emerald-600"
                }`}
              />
              <span
                className={`text-xs ${
                  isDarkMode ? "text-gray-200" : "text-gray-700"
                }`}
              >
                {drugLabels[id] ?? id}
              </span>
            </div>
          ))}

          {logEntries.map((entry) =>
            entry.drugIds.map((id) => (
              <div key={`${entry.ruleId}-${id}`} className="flex items-start gap-2">
                <XCircle
                  className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${
                    isDarkMode ? "text-red-400" : "text-red-500"
                  }`}
                />
                <div>
                  <span
                    className={`text-xs line-through ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {drugLabels[id] ?? id}
                  </span>
                  <p
                    className={`text-xs mt-0.5 ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    {entry.reason}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ManagementPlanCapture({
  planPrompt,
  acknowledgementText,
  isDarkMode,
  initialPlanText = "",
  initialAcknowledged = false,
  onChange,
}: {
  planPrompt: string;
  acknowledgementText: string;
  initialPlanText?: string;
  initialAcknowledged?: boolean;
  isDarkMode: boolean;
  onChange: (planText: string, acknowledged: boolean) => void;
}) {
  const [planText, setPlanText] = useState(initialPlanText);
  const [acknowledged, setAcknowledged] = useState(initialAcknowledged);

  const handlePlanChange = (text: string) => {
    setPlanText(text);
    onChange(text, acknowledged);
  };

  const handleAckChange = (checked: boolean) => {
    setAcknowledged(checked);
    onChange(planText, checked);
  };

  return (
    <div
      className={`mt-4 border rounded-lg p-3 space-y-3 ${
        isDarkMode
          ? "border-amber-700 bg-amber-950/30"
          : "border-amber-200 bg-amber-50"
      }`}
    >
      <div>
        <label
          className={`text-xs font-medium block mb-1 ${
            isDarkMode ? "text-amber-200" : "text-amber-900"
          }`}
        >
          {planPrompt}
        </label>
        <textarea
          rows={3}
          value={planText}
          onChange={(e) => handlePlanChange(e.target.value)}
          placeholder="Document monitoring plan…"
          className={`w-full text-sm rounded-md border px-3 py-2 resize-none focus:outline-none focus-visible:ring-2 ${
            isDarkMode
              ? "border-amber-700 bg-gray-800 text-gray-100 placeholder-gray-500 focus-visible:ring-amber-500"
              : "border-amber-300 bg-white text-gray-900 placeholder-gray-400 focus-visible:ring-amber-400"
          }`}
        />
      </div>
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => handleAckChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-amber-500 cursor-pointer"
        />
        <span
          className={`text-xs ${
            isDarkMode ? "text-amber-200" : "text-amber-900"
          }`}
        >
          {acknowledgementText}
        </span>
      </label>
    </div>
  );
}

export function DrugEliminationNode({
  node,
  eligibleDrugIds,
  drugLabels,
  isDarkMode = false,
  stepNumber,
  value,
  onAnswer,
  onPreviewChange,
  evaluationPatient,
  evaluationContext,
}: DrugEliminationNodeProps) {
  const [selectedAnswer, setSelectedAnswer] = useState<boolean | null>(
    value?.answer ?? null
  );
  const [managementPlan, setManagementPlan] = useState<{
    planText: string;
    patientAcknowledged: boolean;
  } | null>(value?.managementPlan ?? null);

  const computeForAnswer = (answer: boolean) =>
    computeEliminationAnswer({
      node,
      answer,
      eligibleDrugIds,
      patient: evaluationPatient,
      context: evaluationContext ?? {},
    });

  const preview = selectedAnswer !== null ? computeForAnswer(selectedAnswer) : null;

  useEffect(() => {
    if (!onPreviewChange) return;
    if (selectedAnswer === null) {
      onPreviewChange(null);
      return;
    }
    const result = computeForAnswer(selectedAnswer);
    onPreviewChange({
      eligibleDrugIds: eligibleDrugIds.filter(
        (id) => !result.eliminatedDrugIds.includes(id)
      ),
      eliminatedDrugIds: result.eliminatedDrugIds,
      logEntries: result.logEntries.map((e) => ({
        nodeId: node.id,
        ruleId: e.ruleId,
        drugIds: e.drugIds,
        reason: e.reason,
        answer: e.answer,
        eliminatedAt: "",
      })),
    });
  }, [selectedAnswer, eligibleDrugIds, onPreviewChange]);

  const managementPlanTriggered =
    node.managementPlan !== undefined &&
    selectedAnswer !== null &&
    ((node.managementPlan.triggeredByAnswer === "yes" && selectedAnswer === true) ||
      (node.managementPlan.triggeredByAnswer === "no" && selectedAnswer === false));

  const canContinue =
    selectedAnswer !== null &&
    (!managementPlanTriggered ||
      (managementPlan !== null &&
        managementPlan.planText.trim() !== "" &&
        managementPlan.patientAcknowledged));

  const handleContinue = () => {
    if (selectedAnswer === null) return;

    const { eliminatedDrugIds, logEntries } = computeForAnswer(selectedAnswer);

    onAnswer({
      answer: selectedAnswer,
      eliminatedDrugIds,
      logEntries,
      managementPlan: managementPlanTriggered ? managementPlan : null,
    });
  };

  const cardClass = `border rounded-2xl p-4 ${
    isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
  }`;

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-3 mb-4">
        {stepNumber !== undefined && (
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {stepNumber}
          </div>
        )}
        <div className="flex-1">
          <h3
            className={`text-sm font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {node.text}
          </h3>
          {node.hint && (
            <p
              className={`text-xs mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {node.hint}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {([true, false] as const).map((opt) => {
          const selected = selectedAnswer === opt;
          const label = opt ? "Yes" : "No";
          const base =
            "px-4 py-2.5 rounded-lg border-2 text-sm font-semibold transition select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1";

          const selectedCls = getSelectedAnswerClasses(
            node.answerPolarity,
            opt,
            isDarkMode
          );

          const unselectedCls = isDarkMode
            ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600"
            : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50";

          return (
            <button
              key={String(opt)}
              type="button"
              aria-pressed={selected}
              onClick={(e) => {
                setSelectedAnswer(opt);
                setManagementPlan(null);
                (e.currentTarget as HTMLButtonElement).blur();
              }}
              className={`${base} ${selected ? selectedCls : unselectedCls}`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {managementPlanTriggered && node.managementPlan && (
        <ManagementPlanCapture
          planPrompt={node.managementPlan.planPrompt}
          acknowledgementText={node.managementPlan.acknowledgementText}
          isDarkMode={isDarkMode}
          initialPlanText={value?.managementPlan?.planText ?? ""}
          initialAcknowledged={value?.managementPlan?.patientAcknowledged ?? false}
          onChange={(planText, patientAcknowledged) =>
            setManagementPlan({ planText, patientAcknowledged })
          }
        />
      )}

      {preview && preview.eliminatedDrugIds.length > 0 &&
        eligibleDrugIds.filter(
          (id) => !preview.eliminatedDrugIds.includes(id)
        ).length === 0 && (
          <Alert className="mt-3 border-red-200 bg-red-50 text-red-900">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription>
              No drugs remain eligible. Continuing will route to GP referral.
            </AlertDescription>
          </Alert>
        )}

      <FlowPrimaryButton
        label="Continue"
        wrapperClassName="mt-4 flex justify-end"
        onClick={handleContinue}
        disabled={!canContinue}
        blockedReason={
          selectedAnswer === null
            ? "Select an answer to continue."
            : "Complete and acknowledge the management plan to continue."
        }
      />
    </div>
  );
}
